import { ref, computed } from "vue";
import { queryClient } from "@/lib/queryClient";
import { tenantSettingsQueryKey, publicTenantSettingsQueryKey } from "./queryKeys";
import type {
  TenantSettings,
  TenantSettingsResponse,
  TenantSettingsForPublic,
  TenantSettingsForInstitutionWorker,
} from "./types";

const authenticatedData = ref<TenantSettingsResponse | undefined>(
  queryClient.getQueryData<TenantSettingsResponse>(tenantSettingsQueryKey()),
);

const publicData = ref<TenantSettingsForPublic | undefined>(
  queryClient.getQueryData<TenantSettingsForPublic>(publicTenantSettingsQueryKey()),
);

queryClient.getQueryCache().subscribe((event) => {
  const key = event.query.queryKey[0];

  // On a "removed" event (e.g. queryClient.clear() on logout) event.query.state.data still
  // carries the last known value, so reading it here would resurrect the previous session's
  // data into these refs. Treat a removed query as having no data.
  const data = event.type === "removed" ? undefined : event.query.state.data;

  if (key === "tenantSettings") {
    authenticatedData.value = data as TenantSettingsResponse | undefined;
  } else if (key === "publicTenantSettings") {
    publicData.value = data as TenantSettingsForPublic | undefined;
  }
});

// Belt-and-braces alongside the "removed" handling above - called from auth/logout right
// after queryClient.clear() so these module-scope refs never leak into the next session.
export function resetTenantSettingsSelectors(): void {
  authenticatedData.value = undefined;
  publicData.value = undefined;
}

const effectiveData = computed<TenantSettingsResponse | undefined>(
  () => authenticatedData.value ?? publicData.value,
);

// Shared fields — present in both TenantSettings and TenantSettingsForPublic
export const isTicketsModuleEnabled = computed(() => !!effectiveData.value?.enableTicketsModule);

export const diningCardsEnabled = computed(
  () => effectiveData.value?.ordering?.diningCardsEnabled ?? false,
);

export const helpUrl = computed(() => effectiveData.value?.helpModule?.url ?? null);

export const helpUrlLabel = computed(() => effectiveData.value?.helpModule?.urlLabel ?? null);

export const hasHelpUrlAndLabel = computed(
  () => !!effectiveData.value?.helpModule?.url && !!effectiveData.value?.helpModule?.urlLabel,
);

export const allowChangingAllergens = computed(
  () => effectiveData.value?.parents?.allowChangingAllergens ?? false,
);

export const allowCreatingChildren = computed(
  () => effectiveData.value?.parents?.allowCreatingChildren ?? false,
);

export const allowChangingCardNumbers = computed(
  () => effectiveData.value?.parents?.allowChangingCardNumbers ?? false,
);

export const hideInstitutionsListsFromParents = computed(
  () => effectiveData.value?.parents?.hideInstitutionsListsFromParents ?? false,
);

export const hasOnlinePaymentMethod = computed(() => {
  const pm = effectiveData.value?.paymentMethods;
  if (!pm) return false;
  return !!(
    pm.tpayEnabled ||
    pm.autopayEnabled ||
    pm.przelewy24Enabled ||
    pm.payuEnabled ||
    ("tpayCollectEnabled" in pm && pm.tpayCollectEnabled)
  );
});

// Admin-only fields — only present in TenantSettings, always false for non-admin users
export const isBalancePerChildrenEnabled = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.billing?.balancePerChildren ?? false,
);

export const isOrderChargeTimeInOrderDay = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.billing?.orderChargeTime ===
    "in_order_day",
);

export const applyAllowedDebit = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.billing?.applyAllowedDebit ?? false,
);

export const useAccountingDate = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.useAccountingDate ?? false,
);

export const prepaidOrdersAllow = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.ordering?.prepaidOrders?.allow ?? false,
);

// Shared fields added in selector adoption migration
export const tpayEnabled = computed(
  () => effectiveData.value?.paymentMethods?.tpayEnabled ?? false,
);

export const hasTpayCollect = computed(() => {
  const pm = effectiveData.value?.paymentMethods;
  if (!pm || !pm.tpayEnabled) return false;
  return !!("tpayCollectEnabled" in pm && pm.tpayCollectEnabled);
});

// Admin-only — omitted from PaymentMethodsSettingsForUser
export const tpayCollectReceiverName = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.tpayCollectReceiverName ??
    null,
);

export const tpayCollectBankAccountNumber = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods
      ?.tpayCollectBankAccountNumber ?? null,
);

// Not available for Cook (PaymentMethodsSettingsForCook omits codEnabled)
export const codEnabled = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.codEnabled ?? false,
);

export const payuEnabled = computed(
  () => effectiveData.value?.paymentMethods?.payuEnabled ?? false,
);

export const przelewy24Enabled = computed(
  () => effectiveData.value?.paymentMethods?.przelewy24Enabled ?? false,
);

export const autopayEnabled = computed(
  () => effectiveData.value?.paymentMethods?.autopayEnabled ?? false,
);

export const allowChangingAllergensNote = computed(
  () => effectiveData.value?.parents?.allowChangingAllergensNote ?? false,
);

export const allowOnlinePayments = computed(
  () => effectiveData.value?.paymentMethods?.allowOnlinePayments ?? false,
);

export const allowBankTransfers = computed(
  () => effectiveData.value?.paymentMethods?.allowBankTransfers ?? false,
);

export const staticBankAccountNumber = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.staticBankAccountNumber ??
    null,
);

export const staticBankAccountNumber2 = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.staticBankAccountNumber2 ??
    null,
);

// Admin-only
export const orderChargeTime = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.billing?.orderChargeTime ?? null,
);

// Shared billing fields (present in both BillingSettings and PublicBillingSettings)
export const paymentDueDayOfMonth = computed(
  () => effectiveData.value?.billing?.paymentDueDayOfMonth ?? null,
);

export const addCardsFee = computed(() => effectiveData.value?.billing?.addCardsFee ?? false);

export const firstCardFee = computed(() => effectiveData.value?.billing?.firstCardFee ?? null);

export const nextCardFee = computed(() => effectiveData.value?.billing?.nextCardFee ?? null);

// Admin-only billing
export const addManipulationFee = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.billing?.addManipulationFee ?? false,
);

export const defaultManipulationFee = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.billing?.defaultManipulationFee ?? null,
);

export const addPaymentTransactionFee = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.billing?.addPaymentTransactionFee ?? false,
);

export const paymentTransactionFee = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.billing?.paymentTransactionFee ?? null,
);

export const paymentTransactionFeeType = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.billing?.paymentTransactionFeeType ?? null,
);

// Shared parents fields
export const allowSelfRegistration = computed(
  () => effectiveData.value?.parents?.allowSelfRegistration ?? false,
);

export const allowChangingClassrooms = computed(
  () => effectiveData.value?.parents?.allowChangingClassrooms ?? false,
);

export const hideIngredients = computed(
  () => effectiveData.value?.parents?.hideIngredients ?? false,
);

export const allowChangingDiets = computed(
  () => effectiveData.value?.parents?.allowChangingDiets ?? false,
);

export const allowChangingInstitutions = computed(
  () => effectiveData.value?.parents?.allowChangingInstitutions ?? false,
);

// Institution workers self-service class/location change (Admin, InstitutionsAdmin, InstitutionWorker only)
export const allowWorkersChangingClassrooms = computed(
  () =>
    (effectiveData.value as TenantSettings | TenantSettingsForInstitutionWorker | undefined)
      ?.institutionWorkers?.allowChangingClassrooms ?? false,
);

export const allowWorkersChangingInstitutions = computed(
  () =>
    (effectiveData.value as TenantSettings | TenantSettingsForInstitutionWorker | undefined)
      ?.institutionWorkers?.allowChangingInstitutions ?? false,
);

// Not available for Cook (PaymentMethodsSettingsForCook omits minimumPaymentAmount)
export const minimumPaymentAmount = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.paymentMethods?.minimumPaymentAmount ?? {
      amount: 0,
    },
);

// Not available for Cook (TenantSettingsForCook has no accounting field)
export const invoicesEnabledForUsers = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.accounting?.enableInvoicesForUsers ??
    false,
);

export const requireInvoiceConfigForUsers = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.accounting?.requireInvoiceConfigForUsers ??
    false,
);

// Admin-only ordering fields
export const allowAnyDiets = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.ordering?.allowAnyDiets ?? false,
);

export const allowNotes = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.ordering?.allowNotes ?? false,
);

export const consistentDietInDay = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.ordering?.consistentDietInDay ?? false,
);

export const cardNumberLength = computed(
  () => (effectiveData.value as TenantSettings | undefined)?.ordering?.cardNumberLength ?? 10,
);

export const allowSubscribedPayments = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.ordering?.allowSubscribedPayments ?? false,
);

export const allowOnlyOneDietAndMealType = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.ordering?.allowOnlyOneDietAndMealType ??
    false,
);

export const forceKitchensForInstitutionsAssignment = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.forceKitchensForInstitutionsAssignment ??
    false,
);

export const isFiscalizationEnabled = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.accounting?.isFiscalizationEnabled ??
    false,
);

export const fiscalizationSource = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.accounting?.fiscalizationSource ??
    "payments",
);

export const isAnyInvoicesConfigComplete = computed(
  () =>
    (effectiveData.value as TenantSettings | undefined)?.accounting?.isAnyInvoicesConfigComplete ??
    false,
);
